/**
 * What the voice reminder actually did, for the debug panel to show.
 *
 * Deliberately a side channel: nothing here feeds a decision, so watching it cannot change
 * what the app would otherwise do. It exists because a reminder that stays silent leaves no
 * other trace — the interesting failures are all in the platform, not in our arithmetic.
 */

import { useSyncExternalStore } from "react";

export interface SpokenAlertOutcome {
	minutesBefore: number;
	category: "leave" | "train";
	atMs: number;
	status: "queued" | "spoken" | "dropped" | "failed";
}

export interface AlertDiagnostics {
	/** Speech is gesture-gated on iOS; until this is true an utterance is dropped in silence. */
	voicePrimed: boolean;
	wakeLock: "unsupported" | "idle" | "held" | "refused";
	/** Why the screen lock was refused. Low Power Mode is the usual answer on iOS. */
	wakeLockError: string | null;
	/** One outcome per reminder moment; queued is not counted as audible. */
	spokenAlerts: SpokenAlertOutcome[];
	lastPush: { minutesBefore: number | null; atMs: number } | null;
	/** Whatever the speech engine last complained about. */
	speechError: string | null;
	/**
	 * The engine's own view of itself when an alert was handed to it.
	 *
	 * "Queued but never started" has no error event attached, so the queue flags are the only
	 * evidence of why: a paused queue, or one still holding something that never finished.
	 */
	speechQueue: { speaking: boolean; pending: boolean; paused: boolean; voices: number; atMs: number } | null;
	/**
	 * The largest gap seen between the footer and the bottom of the window.
	 *
	 * Kept as a high-water mark because the fault is transient and opening the panel to look at
	 * it may be the very thing that heals it.
	 */
	worstFooterGap: { px: number; atMs: number } | null;
}

let state: AlertDiagnostics = {
	voicePrimed: false,
	wakeLock: typeof navigator !== "undefined" && "wakeLock" in navigator ? "idle" : "unsupported",
	wakeLockError: null,
	spokenAlerts: [],
	lastPush: null,
	speechError: null,
	speechQueue: null,
	worstFooterGap: null,
};

const listeners = new Set<() => void>();

export function noteAlertDiagnostics(patch: Partial<AlertDiagnostics>): void {
	state = { ...state, ...patch };
	for (const listener of listeners) {
		listener();
	}
}

export function mergeSpokenAlert(outcomes: SpokenAlertOutcome[], outcome: SpokenAlertOutcome): SpokenAlertOutcome[] {
	return [...outcomes.filter((entry) => entry.category !== outcome.category || entry.minutesBefore !== outcome.minutesBefore), outcome];
}

export function noteSpokenAlert(outcome: SpokenAlertOutcome): void {
	noteAlertDiagnostics({ spokenAlerts: mergeSpokenAlert(state.spokenAlerts, outcome) });
}

/** Only worth recording when it beats what has been seen already. */
export function noteFooterGap(px: number): void {
	if (px <= 0 || px <= (state.worstFooterGap?.px ?? 0)) {
		return;
	}
	noteAlertDiagnostics({ worstFooterGap: { px, atMs: Date.now() } });
}

export function useAlertDiagnostics(): AlertDiagnostics {
	return useSyncExternalStore(
		(listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		() => state,
		() => state,
	);
}
